import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Grid } from '../components/atoms/Grid/Grid';
import { Text } from '../components/atoms/Typography/Text';
import { EmptyState } from '../components/atoms/EmptyState';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { ParameterTargetCard } from '../components/parameterTargets/ParameterTargetCard';
import { SetPointForm } from '../components/parameterTargets/SetPointForm';
import { TestIntervalsSection } from '../components/parameterTargets/TestIntervalsSection';
import { parameterTargetsKey, useParameterTargets } from '../hooks/useParameterTargets';
import type { ResolvedParameterTarget } from '../services/parameterTargetService';

/**
 * "Zielwerte" (Reef Guard #1819): every parameter's target in force — hobby
 * default or the user's personal set point ± tolerance — with its source, and
 * the set-point editor. These targets drive alerts, the dashboard status, the
 * health score and the chart bands alike. Below: the test intervals (#1823).
 */
export function ParameterTargetsPage() {
  const { t } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const targetsQuery = useParameterTargets(aquariumId);
  const [editing, setEditing] = useState<ResolvedParameterTarget | null>(null);
  const [announcement, setAnnouncement] = useState('');

  function handleSaved(target: ResolvedParameterTarget, cleared: boolean): void {
    const name = String(t(`measurement.history.parameterNames.${target.parameterId}`));
    setEditing(null);
    setAnnouncement(cleared ? t('paramTargets.announce.cleared', { parameter: name }) : t('paramTargets.announce.saved', { parameter: name }));
    void queryClient.invalidateQueries({ queryKey: parameterTargetsKey(aquariumId) });
    // Dashboard status, history bands and the alerts read the same targets.
    void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
  }

  let body;
  if (targetsQuery.isPending) {
    body = <Text color="muted">{t('paramTargets.loading')}</Text>;
  } else if (targetsQuery.isError) {
    body = (
      <LoadErrorState
        title={t('paramTargets.error.loadFailed')}
        onRetry={() => void targetsQuery.refetch()}
        retryLabel={t('paramTargets.error.retry')}
        titleLevel={3}
      />
    );
  } else if (targetsQuery.data.length === 0) {
    body = (
      <EmptyState
        icon={<SlidersHorizontal aria-hidden="true" />}
        headline={t('paramTargets.empty.title')}
        description={t('paramTargets.empty.description')}
        actionLabel={t('paramTargets.empty.action')}
        onAction={() => navigate(`/aquariums/${aquariumId}/measurements/new`)}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <Grid as="ul" minItemWidth="sm" gap="4" className="parameter-targets__list" data-testid="parameter-targets-list">
        {targetsQuery.data.map((target) => (
          <ParameterTargetCard key={target.parameterId} target={target} onEdit={setEditing} />
        ))}
      </Grid>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader title={t('paramTargets.title')} description={t('paramTargets.subtitle')} />
          <Section title={t('paramTargets.section.parameters')}>
            <LiveRegion visuallyHidden busy={targetsQuery.isPending}>{announcement}</LiveRegion>
            {body}
          </Section>
          <TestIntervalsSection aquariumId={aquariumId} />
          <Dialog
            isOpen={editing !== null}
            onClose={() => setEditing(null)}
            title={
              editing
                ? t('paramTargets.editor.title', { parameter: String(t(`measurement.history.parameterNames.${editing.parameterId}`)) })
                : ''
            }
            data-testid="set-point-dialog"
          >
            {editing && (
              <SetPointForm aquariumId={aquariumId} target={editing} onSaved={handleSaved} onCancel={() => setEditing(null)} />
            )}
          </Dialog>
        </PageContent>
      }
    />
  );
}

export default ParameterTargetsPage;
